// Hand-written mirror of the backend GraphQL surface (`rules/dtos/*.ts`, `rules/inputs/*.ts`) —
// there is no FE codegen, so a drifted field name here fails only at runtime.

// `@prisma/client`'s `RuleFamily` enum, mirrored (backend-only package, can't be imported here).
export enum RuleFamily {
  watchlist = 'watchlist',
  colorCoding = 'colorCoding',
  matching = 'matching',
}

// `@prisma/client`'s `RuleSetStatus` enum, mirrored.
export enum RuleSetStatus {
  draft = 'draft',
  published = 'published',
}

// `@prisma/client`'s `RuleType` / `RuleLogic` (SPEC D1/D6).
export enum RuleType {
  exact = 'exact',
  ai = 'ai',
}

export enum RuleLogic {
  any = 'any',
  all = 'all',
}

export enum RuleAppliesTo {
  self = 'self',
  partner = 'partner',
}

export enum RuleTestSource {
  cleanProfile = 'cleanProfile',
  customProfile = 'customProfile',
  application = 'application',
}

// Which control of a condition row a validation message belongs under.
export enum ConditionRowErrorTarget {
  field = 'field',
  operator = 'operator',
  rightField = 'rightField',
  value = 'value',
  points = 'points',
}

export interface ConditionRowIssue {
  target: ConditionRowErrorTarget;
  message: string;
}

// ⚠ GraphQL serialises an enum by its NAME, so these hold `multiSelect`, never the backend's
// underlying `multi_select`.
export enum RuleFieldType {
  boolean = 'boolean',
  select = 'select',
  multiSelect = 'multiSelect',
  text = 'text',
  number = 'number',
  date = 'date',
  list = 'list',
}

// ⚠ Operators travel in BOTH spellings: a GraphQL enum carries the NAME (`includesAny`), stored
// JSON carries the VALUE (`includes_any`). Members below are keyed by name and hold the value;
// `operatorFromWireName` / `operatorWireName` are the only two crossings.
export enum RuleOperator {
  equals = 'equals',
  in = 'in',
  includesAny = 'includes_any',
  isEmpty = 'is_empty',
  contains = 'contains',
  isBlank = 'is_blank',
  isNotBlank = 'is_not_blank',
  textLengthLt = 'text_length_lt',
  textLengthGt = 'text_length_gt',
  blankOrRepeatedCharacters = 'blank_or_repeated_characters',
  lt = 'lt',
  lte = 'lte',
  gt = 'gt',
  gte = 'gte',
  withinLastMonths = 'within_last_months',
  olderThanMonths = 'older_than_months',
  olderThanYears = 'older_than_years',
  countGte = 'count_gte',
  countLte = 'count_lte',
}

export type RuleOperatorName = keyof typeof RuleOperator;

// `RuleFieldOptionDto` / `RuleFieldDto` — one row of the tenant's field catalogue.
export interface RuleFieldOption {
  key: string;
  label: string;
}

export interface RuleField {
  path: string;
  label: string;
  type: RuleFieldType;
  options: RuleFieldOption[];
  appliesTo: RuleAppliesTo;
  derived: string | null;
  // The legal operator subset for this field's type, shipped BY the server as a plain `[String]` in
  // the VALUE spelling of whichever family's vocabulary applies (`RuleOperator` or `ColorOperator`);
  // `legalOperatorsFor` / `legalColorOperatorsFor` narrow it.
  operators: string[];
  // Matching-only: which side of a pair this field belongs to (`gc`/`ip`). `null` for the
  // watchlist/colour catalogues, which have no entity concept.
  entity: string | null;
}

// A stored condition: `operator` is in the stored VALUE spelling.
export interface RuleCondition {
  field: string;
  operator: string;
  value?: unknown;
}

// Stored `Rule.severityOverrides` — ORDER IS SEMANTICS: first match wins.
export interface RuleSeverityOverride {
  logic: RuleLogic;
  conditions: RuleCondition[];
  severity: string;
}

export interface RuleDisplayDirective {
  action?: string;
  raw?: string;
  field?: string;
}

// `RuleDto`. `displayDirective`, `note` and `source` are imported provenance: readable here, never
// authored — no upsert input carries them.
export interface Rule {
  id: number;
  ruleSetVersionId: number;
  ruleId: string;
  ordinal: number;
  type: RuleType;
  ruleText: string;
  severity: string;
  enabled: boolean;
  uiVisible: boolean;
  section: string | null;
  logic: RuleLogic | null;
  conditions: RuleCondition[];
  evidenceFields: string[];
  severityOverrides: RuleSeverityOverride[] | null;
  displayDirective: RuleDisplayDirective[] | null;
  note: string | null;
  source: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface RuleSetVersion {
  id: number;
  family: RuleFamily;
  version: number;
  status: RuleSetStatus;
  parentVersion: number | null;
  restoredFromVersion: number | null;
  authorId: number | null;
  authorName: string | null;
  publishedAt: string | null;
  artifactKey: string | null;
  artifactPublishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  rules?: Rule[];
  // colorCoding-family children — resolved by the SAME query as `rules` above, always present in
  // the response (possibly `[]` on a watchlist version, never `undefined`).
  colorScoringRules?: ColorScoringRule[];
  colorGates?: ColorGate[];
  colorBands?: ColorBand[];
  // matching-family children — resolved by the SAME query as `rules` above (03-matching PLAN).
  matchingRules?: MatchingRule[];
}

// ── Colour coding (color-coding-rules-ui, SAA-105/SAA-150) ─────────────────────────────────────
// `ColorGateKind` / `ColorCodeColor` are Prisma enums registered as GraphQL enums whose NAME equals
// their VALUE — no wire crossing needed (unlike `ColorOperator` below).
export enum ColorGateKind {
  override = 'override',
  cap = 'cap',
}

export enum ColorCodeColor {
  unicorn = 'unicorn',
  green = 'green',
  yellow = 'yellow',
  orange = 'orange',
  red = 'red',
}

// ⚠ `ColorOperator` (backend `ColorOperatorEnum`) IS a registered GraphQL enum whose NAME differs
// from its VALUE for three of five members (`notIn`/`not_in`, `isTrue`/`is_true`,
// `isFalse`/`is_false`) — the same trap `RuleOperator` above documents. Persisted JSON
// (`cases[].operator`, `conditions[].operator`) and the plain `[String]` `RuleField.operators` both
// carry the VALUE spelling; a mutation's `operator` field needs the NAME. `colorOperatorFromWireValue`
// / `colorOperatorWireName` (utils) are the only two crossings.
export enum ColorOperator {
  equals = 'equals',
  in = 'in',
  notIn = 'not_in',
  isTrue = 'is_true',
  isFalse = 'is_false',
}

export type ColorOperatorName = keyof typeof ColorOperator;

// `ColorScoringRuleDto.cases[]` / `ColorGateDto.conditions[].operator` — stored VALUE spelling.
export interface ColorScoringCase {
  operator: string;
  value?: unknown;
  points: number;
}

export interface ColorScoringRule {
  id: number;
  ruleSetVersionId: number;
  ruleId: string;
  ordinal: number;
  type: RuleType;
  ruleText: string;
  enabled: boolean;
  field: string | null;
  points: number | null;
  cases: ColorScoringCase[];
  note: string | null;
  source: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ColorGateCondition {
  field: string;
  operator: string;
  value?: unknown;
}

export interface ColorGate {
  id: number;
  ruleSetVersionId: number;
  gateId: string;
  ordinal: number;
  kind: ColorGateKind;
  order: number;
  color: ColorCodeColor;
  logic: RuleLogic;
  ruleText: string;
  conditions: ColorGateCondition[];
  note: string | null;
  source: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ColorBand {
  id: number;
  ruleSetVersionId: number;
  ordinal: number;
  min: number | null;
  max: number | null;
  color: ColorCodeColor;
  createdAt: string;
  updatedAt: string;
}

// `ColorRubricPreviewDto` — PLAN P11: the derived reachable range plus D4's blocking findings and
// D11/PG2's non-blocking warnings, read BEFORE the publish click.
export interface ColorRubricPreview {
  minScore: number;
  maxScore: number;
  blocking: string[];
  warnings: string[];
}

// ── Colour authoring inputs (`colorScoringRuleUpsertOne` etc.) — `operator` travels as the wire
// NAME here, unlike the read-side interfaces above.
export interface ColorScoringCaseInput {
  operator: ColorOperatorName;
  value?: unknown;
  points: number;
}

export interface ColorScoringRuleUpsertInput {
  id?: number;
  ruleSetVersionId: number;
  type: RuleType;
  ruleText: string;
  enabled: boolean;
  field?: string;
  points?: number;
  cases?: ColorScoringCaseInput[];
}

export interface ColorGateConditionInput {
  field: string;
  operator: ColorOperatorName;
  value?: unknown;
}

export interface ColorGateUpsertInput {
  id?: number;
  ruleSetVersionId: number;
  kind: ColorGateKind;
  order: number;
  color: ColorCodeColor;
  logic: RuleLogic;
  ruleText: string;
  conditions: ColorGateConditionInput[];
}

export interface ColorBandInput {
  min?: number;
  max?: number;
  color: ColorCodeColor;
}

export interface ColorBandsReplaceInput {
  ruleSetVersionId: number;
  bands: ColorBandInput[];
}

// ── Colour dry-run case result ──────────────────────────────────────────────────────────────────
export interface ColorDryRunEvidence {
  path: string;
  label?: string;
  answer?: unknown;
}

export interface ColorDryRunBreakdownRow {
  ruleId?: string;
  ruleText?: string;
  points?: number;
  value?: unknown;
  evidence?: ColorDryRunEvidence[];
}

// One `payload.cases[]` entry for a `colorcoding.dryrun` result — mirrors
// `contracts/colorcoding_dryrun.py`'s `ColorDryRunCaseResult` (camelCase wire aliases).
export interface ColorDryRunCaseResult {
  caseId: string;
  label: string;
  status: 'completed' | 'failed';
  color?: ColorCodeColor | null;
  score?: number | null;
  breakdown?: ColorDryRunBreakdownRow[] | null;
  justification?: string | null;
  failure?: { reason: string; retryable?: boolean } | null;
  tokensIn?: number | null;
  tokensOut?: number | null;
  // `skipped` is deliberately NOT read from here — the typed `RuleDryRun.cases[].skipped` field
  // (`DryRunCase`/`RuleDryRunSkipReason` below) is the single path for BOTH families now that
  // `RuleDryRunFieldResolver` normalises by family.
}

// ── Matching (03-matching, SAA-172/SAA-193) ─────────────────────────────────────────────────────
// `MatchingRuleKind` / `MatchingEntity` are registered GraphQL enums whose NAME equals their VALUE
// — no wire crossing needed (unlike `MatchingOperator`/`MatchingGrade` below).
export enum MatchingRuleKind {
  exclusion = 'exclusion',
  blocking = 'blocking',
  compatibility = 'compatibility',
}

export enum MatchingEntity {
  gc = 'gc',
  ip = 'ip',
}

// FE-only: which `RulesDryRunStartInput` matching shape the dry-run bench sends (SPEC §7.5).
export enum MatchingPairMode {
  pair = 'pair',
  gcFanOut = 'gcFanOut',
  ipFanOut = 'ipFanOut',
  bothFanOut = 'bothFanOut',
}

// FE-only: a matching rule source row picks a catalogue field OR a Notes section.
export enum MatchingSourceKind {
  field = 'field',
  notes = 'notes',
}

// The Notes headings a matching source may read — mirrors the engine's `KNOWN_HEADINGS`
// (`engine/matching/notes.py`) and the backend's `MatchingNotesSectionEnum`, spelled exactly: any
// other heading silently never matches.
export enum MatchingNotesSection {
  gcProfilesDenied = 'GC Profiles Denied and Reason',
  ipProfilesDenied = 'IP Profiles Denied and Reason',
  notesForMatching = 'NOTES FOR MATCHING',
  blurb = 'BLURB',
}

// Engine output vocabularies read off the dry-run payload (`contracts/matching_dryrun.py`).
export enum MatchingBreakdownCounted {
  full = 'full',
  half = 'half',
  zero = 'zero',
}

export enum MatchingDryRunCaseStatus {
  completed = 'completed',
  failed = 'failed',
}

export enum MatchingModelIdSource {
  artifact = 'artifact',
  override = 'override',
}

// ⚠ `MatchingGrade` (backend `MatchingGradeEnum`) has a NAME/VALUE split for one member
// (`notExcluded`/`not_excluded`) — same trap as `RuleOperator`/`ColorOperator` above.
export enum MatchingGrade {
  excluded = 'excluded',
  notExcluded = 'not_excluded',
  clear = 'clear',
  blocking = 'blocking',
  match = 'match',
  partial = 'partial',
  mismatch = 'mismatch',
}

export type MatchingGradeName = keyof typeof MatchingGrade;

// ⚠ `MatchingOperator` (backend `MatchingOperatorEnum`) has a NAME/VALUE split for most members
// (`notIn`/`not_in`, `isTrue`/`is_true`, …) — same trap as `RuleOperator`/`ColorOperator` above.
// Persisted JSON (`grades[].conditions[].operator`) and the plain `[String]` `RuleField.operators`
// both carry the VALUE spelling; a mutation's `operator` field needs the NAME.
export enum MatchingOperator {
  equals = 'equals',
  in = 'in',
  notIn = 'not_in',
  isTrue = 'is_true',
  isFalse = 'is_false',
  isBlank = 'is_blank',
  isNotBlank = 'is_not_blank',
  gte = 'gte',
  lte = 'lte',
  includesAny = 'includes_any',
  equalsField = 'equals_field',
  includesAnyField = 'includes_any_field',
  dateDiffWithinDays = 'date_diff_within_days',
  dateDiffWithinMonths = 'date_diff_within_months',
}

export type MatchingOperatorName = keyof typeof MatchingOperator;

// One entry of a matching rule's `sources[]` — EITHER a catalogue `field` path OR a Notes
// `notesSection` name, never both (checked server-side, not by a single property).
export interface MatchingRuleSource {
  entity: MatchingEntity;
  field?: string;
  notesSection?: string;
}

// Kind-specific grade-definition TEXT — which keys are required depends on the rule's `kind`.
export interface MatchingDefinitions {
  excluded?: string;
  notExcluded?: string;
  clear?: string;
  blocking?: string;
  match?: string;
  partial?: string;
  mismatch?: string;
}

export interface MatchingFieldRef {
  entity: MatchingEntity;
  field: string;
}

// One condition of an `exact` matching rule's `grades[]` group. `right` is present only for a
// cross-entity operator; `value` is present only for a single-entity, non-valueless operator.
export interface MatchingCondition {
  left: MatchingFieldRef;
  operator: string;
  right?: MatchingFieldRef;
  value?: unknown;
}

// One ordered grade/condition group of an `exact` matching rule — first whose conditions hold wins.
export interface MatchingGradeGroup {
  grade: string;
  logic: RuleLogic;
  conditions: MatchingCondition[];
}

// `MatchingRuleDto`. `note`/`source` are imported provenance: readable, never authored.
export interface MatchingRule {
  id: number;
  ruleSetVersionId: number;
  ruleId: string;
  ordinal: number;
  kind: MatchingRuleKind;
  type: RuleType;
  name: string;
  ruleText: string;
  weight: number | null;
  enabled: boolean;
  pairsWith: string | null;
  sources: MatchingRuleSource[];
  definitions: MatchingDefinitions;
  grades: MatchingGradeGroup[];
  note: string | null;
  source: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

// `MatchingRulesPreviewDto` — the active weights total plus V-2…V-8's blocking findings and V-1's
// (never-blocking) warning, read BEFORE the publish click.
export interface MatchingRulesPreview {
  activeWeightTotal: number;
  blocking: string[];
  warnings: string[];
}

// ── Matching authoring inputs (`matchingRuleUpsert`) — `operator`/`grade` travel as the wire NAME. ─
export interface MatchingRuleSourceInput {
  entity: MatchingEntity;
  field?: string;
  notesSection?: string;
}

export interface MatchingFieldRefInput {
  entity: MatchingEntity;
  field: string;
}

export interface MatchingConditionInput {
  left: MatchingFieldRefInput;
  operator: MatchingOperatorName;
  right?: MatchingFieldRefInput;
  value?: unknown;
}

export interface MatchingGradeGroupInput {
  grade: MatchingGradeName;
  logic: RuleLogic;
  conditions: MatchingConditionInput[];
}

export interface MatchingRuleUpsertInput {
  id?: number;
  ruleSetVersionId: number;
  kind: MatchingRuleKind;
  type: RuleType;
  name: string;
  ruleText: string;
  weight?: number;
  enabled: boolean;
  pairsWith?: string;
  sources: MatchingRuleSourceInput[];
  definitions: MatchingDefinitions;
  grades?: MatchingGradeGroupInput[];
}

export interface MatchingRulesBulkDeleteInput {
  ruleSetVersionId: number;
  ruleRowIds?: number[];
}

// ── Matching dry-run case result (`matching.dryrun`) ────────────────────────────────────────────
// One `payload.cases[]` entry for a matching dry run — mirrors the engine's per-pair result
// contract (SPEC §3.1/§3.2). `skipped` is deliberately NOT read from here — the typed
// `RuleDryRun.cases[].skipped` field (`DryRunCase` above) is the single path for all three families
// now that `RuleDryRunFieldResolver` normalises it by family.
export interface MatchingDryRunExcluded {
  ruleId: string;
  name: string;
  reason: string;
}

export interface MatchingDryRunBreakdownRow {
  ruleId: string;
  name: string;
  kind: MatchingRuleKind;
  grade: string;
  weight: number;
  pointsEarned: number;
  counted?: MatchingBreakdownCounted;
}

// A named-but-unresolvable source, catalogue-path shaped — never a value (SPEC §3.9's
// `dataGaps`/`entityDataGaps` are catalogue paths, not prose).
export interface MatchingDataGap {
  entity: MatchingEntity;
  field: string;
}

export interface MatchingDryRunCaseResult {
  caseId: string;
  label: string;
  status: MatchingDryRunCaseStatus;
  score?: number | null;
  weightApplied?: number | null;
  pointsEarned?: number | null;
  excluded?: MatchingDryRunExcluded | null;
  breakdown?: MatchingDryRunBreakdownRow[] | null;
  hardFails?: string[] | null;
  pros?: string[] | null;
  cons?: string[] | null;
  summary?: string | null;
  dataGaps?: MatchingDataGap[] | null;
  failure?: { reason: string; retryable?: boolean } | null;
  tokensIn?: number | null;
  tokensOut?: number | null;
  durationMs?: number | null;
}

// The dry-run result envelope's `payload.metrics` — matching-only, schema-opaque on the wire
// (`MatchingDryRunResult.metrics: dict[str, Any]`, `contracts/matching_dryrun.py`) — same numbers
// as the engine's `MATCHING_METRICS=` log line (`engine/matching/metrics.py::build_metrics`, the
// source of these field names, re-read 2026-09-14). `RuleDryRunDto` still carries no typed
// `metrics` field (nor per-case `tokensIn`/`tokensOut`/`durationMs`) — only `payload: GraphQLJSON`
// — so this is read off `DryRunPayload.metrics`/`MatchingDryRunCaseResult` the same way the colour
// family's `tokensIn`/`tokensOut` were always read.
export interface MatchingDryRunCallStat {
  mean: number | null;
  min: number | null;
  max: number | null;
}

export interface MatchingDryRunMetrics {
  task?: string | null;
  taskType?: string | null;
  tenant?: string | null;
  configVersion?: string | null;
  artifactPromptVersion?: string | null;
  modelId?: string | null;
  // `resolve_matching_model_id` — "artifact" is the published rule set's own pin, "override" is the
  // dev-only `AI_MATCHING_MODEL_OVERRIDE` env knob (unpriced overrides read `estimatedCostUsd: null`).
  modelIdSource?: MatchingModelIdSource | null;
  concurrency?: number | null;
  total?: number | null;
  excluded?: number | null;
  // digest-phase calls vs grade-phase (per-pair) calls — the grade count is the per-pair cost driver.
  modelCalls?: { digest: number; grade: number } | null;
  cacheHits?: number | null;
  tokensIn?: number | null;
  tokensOut?: number | null;
  pairCallTokensIn?: MatchingDryRunCallStat | null;
  pairCallTokensOut?: MatchingDryRunCallStat | null;
  callDurationMs?: MatchingDryRunCallStat | null;
  sumCallDurationsMs?: number | null;
  wallMs?: number | null;
  parallelismRatio?: number | null;
  estimatedCostUsd?: number | null;
}

export interface RulesFamilySummary {
  family: RuleFamily;
  publishedVersion: number | null;
  publishedAt: string | null;
  draftVersion: number | null;
  ruleCount: number;
  // Whether the rules UI may WRITE this family — the backend's `RULES_UI_EDITABLE_FAMILIES`, so the
  // FE never re-hardcodes the list.
  editable: boolean;
}

export type DryRunStatus = 'pending' | 'completed' | 'failed';

// Mirrors `contracts/dryrun.py`'s `DryRunCaseResult` — every name below is that model's camelCase
// wire alias, not a shape this FE invents.
export interface DryRunTrigger {
  question: string;
  answer: string;
  appliesTo: string;
}

export interface DryRunFinding {
  ruleId: string;
  title: string;
  severity: string;
  uiVisible: boolean;
  evaluation: string;
  triggers: DryRunTrigger[];
  justification: string;
  displayDirective?: string | null;
}

export interface DryRunCounts {
  total: number;
  uiVisible: number;
  hidden: number;
}

export interface DryRunCaseResult {
  caseId: string;
  label: string;
  status: 'completed' | 'failed';
  counts?: DryRunCounts | null;
  findings?: DryRunFinding[] | null;
  failure?: { reason: string; retryable?: boolean } | null;
  tokensIn?: number | null;
  tokensOut?: number | null;
}

// `RuleDryRunSkipReasonEnum`. ⚠ Arrives as the enum NAME (`fieldAbsent`), never the backend's
// underlying `field_absent` — same trap as `RuleFieldType` above. This one GraphQL enum now serves
// ALL THREE families' dry runs (the shared `RuleDryRun.cases[].skipped` field) — the first five
// members are the watchlist's own vocabulary, unchanged; six more are the colour family's
// (`fieldAbsent`'s wire value is shared, so it is not repeated); the rest are the matching
// family's own vocabulary (`ruleDisabled`/`modelUnavailable` are shared spellings, not repeated).
// `gcSourceAbsent`/`insufficientInformation` (SAA-229): a MODEL `not_applicable` verdict,
// attributed to whichever side actually said nothing — `noPositionStated` keeps its
// IP-specific meaning (both the pairing path AND the model path when the IP side is blank).
export enum RuleDryRunSkipReason {
  disabled = 'disabled',
  fieldAbsent = 'fieldAbsent',
  unresolved = 'unresolved',
  inferenceDeferred = 'inferenceDeferred',
  notApplicable = 'notApplicable',
  ruleDisabled = 'ruleDisabled',
  ruleTypeUnsupported = 'ruleTypeUnsupported',
  noCaseMatched = 'noCaseMatched',
  gateFieldAbsent = 'gateFieldAbsent',
  gateNotMatched = 'gateNotMatched',
  modelUnavailable = 'modelUnavailable',
  sourceAbsent = 'sourceAbsent',
  pairedRuleLive = 'pairedRuleLive',
  noPositionStated = 'noPositionStated',
  noGradeMatched = 'noGradeMatched',
  gcSourceAbsent = 'gcSourceAbsent',
  insufficientInformation = 'insufficientInformation',
}

// `RuleDryRunSkippedRuleDto`. `detail` is a field path plus an operator — the engine guarantees no
// applicant answer, so it is safe to render. `name` is the matching family's own addition (the
// watchlist/colour writers never populate it) — the bench falls back to `ruleId` when absent.
export interface DryRunSkippedRule {
  ruleId: string;
  reason: RuleDryRunSkipReason;
  detail: string | null;
  name?: string | null;
}

// `RuleDryRunCaseDto` — the TYPED slice of a case; `counts`/`findings`/`failure` still come from the
// `payload` JSON, so the two halves are joined by `caseId`.
export interface DryRunCase {
  caseId: string;
  label: string;
  status: string;
  skipped: DryRunSkippedRule[];
}

// What the per-rule Test reports. "Did not fire" and "could not be evaluated" are different
// answers: only the first says anything about the rule's logic.
export type RuleTestOutcome = 'fired' | 'notFired' | 'unevaluable';

// `cases` holds `DryRunCaseResult[]` for a watchlist run, `ColorDryRunCaseResult[]` for a
// `colorcoding.dryrun` one, and `MatchingDryRunCaseResult[]` for a `matching.dryrun` one — the
// caller always knows the family already, so it casts at the one branch point rather than this
// interface trying to discriminate the union itself.
export interface DryRunPayload {
  cases: Array<DryRunCaseResult | ColorDryRunCaseResult | MatchingDryRunCaseResult>;
  mode?: string | null;
  promptVersion?: string | null;
  tokensIn?: number | null;
  tokensOut?: number | null;
  suggestionsFailure?: string | null;
  subjectSource?: 'synthetic' | 'realProfile';
  // Matching-only — see `MatchingDryRunMetrics`.
  metrics?: MatchingDryRunMetrics | null;
}

export interface RuleDryRun {
  dryRunId: string;
  family: RuleFamily;
  mode: string;
  status: string;
  requestedById: number | null;
  payload: DryRunPayload | null;
  cases: DryRunCase[];
  suggestions: string | null;
  failureReason: string | null;
  requestedAt: string;
  completedAt: string | null;
}

export interface DryRunCaseInput {
  caseId: string;
  label: string;
  profile: Record<string, unknown>;
}

// ── Rule authoring (`ruleUpsertOne`, `ruleTestRun`) ─────────────────────────────────────────────
// `operator` is a GraphQL enum here, so it travels as the wire NAME.
export interface RuleConditionInput {
  field: string;
  operator: RuleOperatorName;
  value?: unknown;
}

export interface RuleSeverityOverrideInput {
  logic: RuleLogic;
  conditions: RuleConditionInput[];
  severity: string;
}

// `RuleUpsertInput` — one input for create AND edit. `id` absent = create.
export interface RuleUpsertInput {
  id?: number;
  ruleSetVersionId: number;
  type: RuleType;
  ruleText: string;
  severity: string;
  enabled: boolean;
  uiVisible: boolean;
  section?: string;
  logic?: RuleLogic;
  conditions?: RuleConditionInput[];
  evidenceFields?: string[];
  severityOverrides?: RuleSeverityOverrideInput[];
}

export interface RuleTestRunInput {
  ruleRowId: number;
  source: RuleTestSource;
  profile?: Record<string, unknown>;
  applicationId?: number;
}

// Which value control a condition row gets — see `getConditionValueControl`.
export type ConditionValueControl =
  | 'none'
  | 'boolean'
  | 'optionSelect'
  | 'optionMultiSelect'
  | 'textList'
  | 'text'
  | 'number';

// One condition row while it is being edited. Values are held as STRINGS and typed on the way out
// (`toConditionInput`), so a half-typed number can never reach the mutation as `NaN`.
export interface ConditionFormValue {
  uid: string;
  field: string;
  operator: RuleOperator | '';
  value: string;
  values: string[];
}

export interface SeverityOverrideFormValue {
  uid: string;
  logic: RuleLogic;
  severity: string;
  conditions: ConditionFormValue[];
}
